/**
 * AddPersonModal Component
 *
 * Modal for adding a new person to the tree.
 * Features:
 * - Non-dismissible (no click outside to close)
 * - Full person form with validation
 * - Image upload support
 * - Professional styling
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { validateImageFile, safeValidate, personDataSchema, getFirstErrorMessage } from '@/lib/validation';

interface AddPersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    personData: PersonFormData,
    profileImageFile: File | null,
    partnerSelection?: PartnerSelection,
    coupleMetadata?: CoupleMetadata
  ) => Promise<void>;
  relationshipContext?: {
    type: 'father' | 'mother' | 'son' | 'daughter' | 'spouse' | 'brother' | 'sister';
    relativeName: string;
    allowedGenders?: ('male' | 'female')[]; // For spouse/sibling gender restrictions
  };
  availablePartners?: AvailablePartner[]; // For child partner selection
}

export interface AvailablePartner {
  id: string;
  name: string;
}

export interface PartnerSelection {
  type: 'existing' | 'new';
  partnerId?: string; // For existing partner
}

export interface CoupleMetadata {
  date_start?: number;  // Year only (YYYY)
  date_end?: number;    // Year only (YYYY)
  is_married?: boolean;
  has_ended?: boolean;
}

export interface PersonFormData {
  first_name: string;
  last_name: string;
  nickname: string;
  gender: 'male' | 'female' | 'other';
  date_of_birth: string;
  date_of_death: string;
  marital_status: 'single' | 'married' | 'divorced' | 'widowed' | null;
  birth_place: string;
  death_place: string;
  notes: string;
}

export function AddPersonModal({
  isOpen,
  onClose,
  onSubmit,
  relationshipContext,
  availablePartners,
}: AddPersonModalProps) {
  const { t } = useTranslation('common');

  const [isSaving, setIsSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);

  // Partner selection state (for son/daughter)
  const [selectedPartner, setSelectedPartner] = useState<string>(''); // '' = none, 'new' = create new, or partner ID

  // Couple metadata state (for spouse only)
  const [coupleMetadata, setCoupleMetadata] = useState<CoupleMetadata>({
    date_start: undefined,
    date_end: undefined,
    is_married: false,
    has_ended: false,
  });

  // Living status state (separate from date_of_death to control visibility)
  const [isLiving, setIsLiving] = useState<boolean>(true);

  // Determine initial gender based on relationship context
  const getInitialGender = (): 'male' | 'female' | 'other' => {
    if (!relationshipContext) return 'other';

    switch (relationshipContext.type) {
      case 'father':
        return 'male';
      case 'mother':
        return 'female';
      case 'son':
        return 'male';
      case 'daughter':
        return 'female';
      case 'brother':
        return 'male';
      case 'sister':
        return 'female';
      case 'spouse':
        // For spouse, default to first allowed gender or 'male'
        const allowedGenders = relationshipContext.allowedGenders;
        if (allowedGenders && allowedGenders.length > 0) {
          const firstGender = allowedGenders[0];
          if (firstGender) {
            return firstGender;
          }
        }
        // Fallback to 'male' if no allowedGenders specified
        return 'male';
      default:
        return 'other';
    }
  };

  // Form state
  const [formData, setFormData] = useState<PersonFormData>({
    first_name: '',
    last_name: '',
    nickname: '',
    gender: getInitialGender(),
    date_of_birth: '',
    date_of_death: '',
    marital_status: null,
    birth_place: '',
    death_place: '',
    notes: '',
  });

  // Update gender when modal opens with new relationship context
  useEffect(() => {
    if (isOpen) {
      const initialGender = getInitialGender();
      setFormData(prev => ({ ...prev, gender: initialGender }));
    }
  }, [isOpen, relationshipContext?.type]); // Re-run when modal opens or relationship type changes

  const handleInputChange = (field: keyof PersonFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Use centralized image validation
      const validationResult = validateImageFile(file, 5);
      if (!validationResult.valid) {
        toast.error(validationResult.error);
        return;
      }

      setProfileImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Use centralized validation schema
    const validationResult = safeValidate(personDataSchema, formData);
    if (!validationResult.success) {
      const errorMessage = getFirstErrorMessage(validationResult.errors);
      toast.error(errorMessage);
      return;
    }

    setIsSaving(true);

    try {
      // Build partner selection if applicable
      let partnerSelection: PartnerSelection | undefined;
      if (
        (relationshipContext?.type === 'son' || relationshipContext?.type === 'daughter') &&
        selectedPartner
      ) {
        if (selectedPartner === 'new') {
          partnerSelection = { type: 'new' };
        } else {
          partnerSelection = { type: 'existing', partnerId: selectedPartner };
        }
      }

      // Build couple metadata if applicable (spouse only)
      let coupleMeta: CoupleMetadata | undefined;
      if (relationshipContext?.type === 'spouse') {
        // Validate year inputs if provided
        if (coupleMetadata.date_start !== undefined && coupleMetadata.date_start !== null) {
          const startYear = coupleMetadata.date_start;
          if (isNaN(startYear) || startYear < 1000 || startYear > 2100) {
            toast.error('Start year must be between 1000 and 2100');
            setIsSaving(false);
            return;
          }
        }
        if (coupleMetadata.date_end !== undefined && coupleMetadata.date_end !== null) {
          const endYear = coupleMetadata.date_end;
          if (isNaN(endYear) || endYear < 1000 || endYear > 2100) {
            toast.error('End year must be between 1000 and 2100');
            setIsSaving(false);
            return;
          }
        }
        // Validate year order if both provided
        if (
          coupleMetadata.date_start !== undefined &&
          coupleMetadata.date_end !== undefined &&
          coupleMetadata.date_start !== null &&
          coupleMetadata.date_end !== null &&
          coupleMetadata.date_end < coupleMetadata.date_start
        ) {
          toast.error('End year must be after or equal to start year');
          setIsSaving(false);
          return;
        }

        coupleMeta = {
          date_start: coupleMetadata.date_start,
          date_end: coupleMetadata.date_end,
          is_married: coupleMetadata.is_married,
          has_ended: coupleMetadata.has_ended,
        };
      }

      await onSubmit(formData, profileImageFile, partnerSelection, coupleMeta);

      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        nickname: '',
        gender: getInitialGender(),
        date_of_birth: '',
        date_of_death: '',
        marital_status: null,
        birth_place: '',
        death_place: '',
        notes: '',
      });
      setImagePreview(null);
      setProfileImageFile(null);
      setSelectedPartner('');
      setCoupleMetadata({
        date_start: undefined,
        date_end: undefined,
        is_married: false,
        has_ended: false,
      });
      setIsLiving(true);
    } catch (error) {
      console.error('Failed to add person:', error);
      // Error already handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;

    // Reset form
    setFormData({
      first_name: '',
      last_name: '',
      nickname: '',
      gender: getInitialGender(),
      date_of_birth: '',
      date_of_death: '',
      marital_status: null,
      birth_place: '',
      death_place: '',
      notes: '',
    });
    setImagePreview(null);
    setProfileImageFile(null);
    setSelectedPartner('');
    setCoupleMetadata({
      date_start: undefined,
      date_end: undefined,
      is_married: false,
      has_ended: false,
    });
    setIsLiving(true);

    onClose();
  };

  if (!isOpen) return null;

  // Get translated relationship type name
  const getTranslatedRelationshipType = (type: string): string => {
    switch(type) {
      case 'father':
        return t('tree.relationshipChooser.relationships.father.label');
      case 'mother':
        return t('tree.relationshipChooser.relationships.mother.label');
      case 'son':
        return t('tree.relationshipChooser.relationships.son.label');
      case 'daughter':
        return t('tree.relationshipChooser.relationships.daughter.label');
      case 'spouse':
        return t('tree.relationshipChooser.relationships.spouse.label');
      case 'brother':
        return t('tree.relationshipChooser.relationships.brother.label');
      case 'sister':
        return t('tree.relationshipChooser.relationships.sister.label');
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const modalTitle = relationshipContext
    ? t('tree.addPersonModal.titleWithContext', {
        relationship: getTranslatedRelationshipType(relationshipContext.type),
        name: relationshipContext.relativeName
      })
    : t('tree.addPersonModal.title');

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 md:p-6 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl md:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-xl md:max-w-2xl max-h-[95vh] sm:max-h-[90vh] md:max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 p-3 sm:p-4 md:p-6 border-b border-primary/20">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
              <div className="size-9 sm:size-10 md:size-12 rounded-full bg-primary/20 dark:bg-primary/30 flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-primary text-lg sm:text-xl md:text-2xl">
                  person_add
                </span>
              </div>
              <div className="flex-1 pt-0.5 sm:pt-0.5 md:pt-1">
                <h3 className="text-sm sm:text-base md:text-lg font-bold text-slate-900 dark:text-white mb-0.5 md:mb-1 leading-tight">
                  {modalTitle}
                </h3>
                <p className="text-xs sm:text-xs md:text-sm text-slate-600 dark:text-slate-400 leading-snug">
                  {t('tree.addPersonModal.subtitle')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6 overflow-y-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-160px)] md:max-h-[calc(90vh-180px)] custom-scrollbar">
          <div className="space-y-4 sm:space-y-5">
            {/* Section Header: Person Information */}
            <div className="pb-1.5 sm:pb-2 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-white">
                {t('tree.addPersonModal.sections.personInfo')}
              </h3>
            </div>

            {/* Profile Image Upload */}
            <div className="space-y-1.5 sm:space-y-2">
              <label className="block text-xs sm:text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.profileImage')}
              </label>
              <div className="flex items-start gap-2 sm:gap-3">
                {/* Image Preview */}
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-700 flex-shrink-0 bg-slate-50 dark:bg-slate-800">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-2xl text-slate-300 dark:text-slate-600">
                        {formData.gender === 'male' ? 'man' : formData.gender === 'female' ? 'woman' : 'person'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1">
                  <input
                    type="file"
                    id="image-upload-add"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="image-upload-add"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-md text-xs font-medium cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">upload</span>
                    {t('tree.addPersonModal.chooseImage')}
                  </label>
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setProfileImageFile(null);
                      }}
                      className="ml-2 text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      {t('tree.addPersonModal.removeImage')}
                    </button>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {t('tree.addPersonModal.imageConstraints')}
                  </p>
                </div>
              </div>
            </div>

            {/* First Name */}
            <div className="space-y-1.5">
              <label htmlFor="first_name" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.firstName')} <span className="text-red-500">*</span>
              </label>
              <input
                id="first_name"
                type="text"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                placeholder={t('tree.addPersonModal.firstName')}
                required
              />
            </div>

            {/* Last Name */}
            <div className="space-y-1.5">
              <label htmlFor="last_name" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.lastName')} <span className="text-red-500">*</span>
              </label>
              <input
                id="last_name"
                type="text"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                placeholder={t('tree.addPersonModal.lastName')}
                required
              />
            </div>

            {/* Nickname */}
            <div className="space-y-1.5">
              <label htmlFor="nickname" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.nickname')}
              </label>
              <input
                id="nickname"
                type="text"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                placeholder={t('tree.addPersonModal.nicknamePlaceholder')}
              />
            </div>

            {/* Gender */}
            <div className="space-y-1.5">
              <label htmlFor="gender" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.gender')} <span className="text-red-500">*</span>
              </label>
              <select
                id="gender"
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                required
                disabled={
                  // Disable if gender is locked by relationship type
                  relationshipContext?.allowedGenders?.length === 1 ||
                  relationshipContext?.type === 'brother' ||
                  relationshipContext?.type === 'sister' ||
                  relationshipContext?.type === 'father' ||
                  relationshipContext?.type === 'mother'
                }
              >
                <option value="male">{t('gender.male')}</option>
                <option value="female">{t('gender.female')}</option>
                {/* Only show 'other' option if not restricted by relationship */}
                {!relationshipContext?.allowedGenders &&
                 relationshipContext?.type !== 'brother' &&
                 relationshipContext?.type !== 'sister' &&
                 relationshipContext?.type !== 'father' &&
                 relationshipContext?.type !== 'mother' && (
                  <option value="other">{t('gender.other')}</option>
                )}
              </select>
            </div>

            {/* Other Parent Selection (for Son/Daughter only) */}
            {(relationshipContext?.type === 'son' || relationshipContext?.type === 'daughter') && (
              <>
                {/* Section Header: Other Parent */}
                <div className="pt-3 sm:pt-4 pb-1.5 sm:pb-2 border-b border-slate-200 dark:border-slate-700">
                  <h3 className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-white mb-0.5 sm:mb-1">
                    {t('tree.addPersonModal.sections.otherParent')}
                  </h3>
                  <p className="text-xs sm:text-xs text-slate-600 dark:text-slate-400 leading-snug">
                    {t('tree.addPersonModal.sections.otherParentHint')}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="partner" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    {t('tree.addPersonModal.otherParent')}
                  </label>
                <select
                  id="partner"
                  value={selectedPartner}
                  onChange={(e) => setSelectedPartner(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                >
                  <option value="">{t('tree.addPersonModal.noOtherParent')}</option>
                  <option value="new">{t('tree.addPersonModal.newOtherParent')}</option>
                  {availablePartners && availablePartners.length > 0 && (
                    <>
                      <option disabled>──────────</option>
                      {availablePartners.map(partner => (
                        <option key={partner.id} value={partner.id}>
                          {partner.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {selectedPartner === 'new' && (
                  <p className="text-xs text-primary font-medium mt-1 flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">info</span>
                    {t('tree.addPersonModal.newOtherParentInfo')}
                  </p>
                )}
              </div>
              </>
            )}

            {/* Couple Metadata (for Spouse only) */}
            {relationshipContext?.type === 'spouse' && (
              <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-primary text-lg">favorite</span>
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                    {t('tree.addPersonModal.coupleMetadata.title')} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">({t('common.optional')})</span>
                  </h4>
                </div>

                {/* Start Year */}
                <div className="space-y-1.5">
                  <label htmlFor="couple_start_year" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    {t('tree.addPersonModal.coupleMetadata.startYear')} <span className="text-xs font-normal text-slate-500">({t('common.optional')})</span>
                  </label>
                  <input
                    id="couple_start_year"
                    type="number"
                    min="1000"
                    max="2100"
                    value={coupleMetadata.date_start ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCoupleMetadata(prev => ({
                        ...prev,
                        date_start: value ? parseInt(value, 10) : undefined,
                      }));
                    }}
                    placeholder="YYYY"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('tree.addPersonModal.coupleMetadata.startYearHint')}</p>
                </div>

                {/* End Year */}
                <div className="space-y-1.5">
                  <label htmlFor="couple_end_year" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    {t('tree.addPersonModal.coupleMetadata.endYear')} <span className="text-xs font-normal text-slate-500">({t('common.optional')})</span>
                  </label>
                  <input
                    id="couple_end_year"
                    type="number"
                    min="1000"
                    max="2100"
                    value={coupleMetadata.date_end ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCoupleMetadata(prev => ({
                        ...prev,
                        date_end: value ? parseInt(value, 10) : undefined,
                      }));
                    }}
                    placeholder="YYYY"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('tree.addPersonModal.coupleMetadata.endYearHint')}</p>
                </div>

                {/* Marriage Status Checkboxes */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_married"
                      checked={coupleMetadata.is_married}
                      onChange={(e) => {
                        setCoupleMetadata(prev => ({
                          ...prev,
                          is_married: e.target.checked,
                        }));
                      }}
                      className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-2 focus:ring-primary"
                    />
                    <label htmlFor="is_married" className="text-sm text-slate-700 dark:text-slate-300">
                      {t('tree.addPersonModal.coupleMetadata.isMarried')} <span className="text-xs text-slate-500">{t('tree.addPersonModal.coupleMetadata.isMarriedHint')}</span>
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="has_ended"
                      checked={coupleMetadata.has_ended}
                      onChange={(e) => {
                        setCoupleMetadata(prev => ({
                          ...prev,
                          has_ended: e.target.checked,
                        }));
                      }}
                      className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-2 focus:ring-primary"
                    />
                    <label htmlFor="has_ended" className="text-sm text-slate-700 dark:text-slate-300">
                      {t('tree.addPersonModal.coupleMetadata.hasEnded')} <span className="text-xs text-slate-500">{t('tree.addPersonModal.coupleMetadata.hasEndedHint')}</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Date of Birth */}
            <div className="space-y-1.5">
              <label htmlFor="date_of_birth" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.dateOfBirth')}
              </label>
              <input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>

            {/* Living Status Checkbox */}
            <div className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                id="is-living-add"
                checked={isLiving}
                onChange={(e) => {
                  setIsLiving(e.target.checked);
                  if (e.target.checked) {
                    // If marking as living, clear death date
                    handleInputChange('date_of_death', '');
                  }
                }}
                className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="is-living-add" className="text-sm text-slate-700 dark:text-slate-300 font-medium">
                {t('tree.addPersonModal.personIsLiving')}
              </label>
            </div>

            {/* Date of Death (conditional) */}
            {!isLiving && (
              <div className="space-y-1.5">
                <label htmlFor="date_of_death" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                  {t('tree.addPersonModal.dateOfDeath')}
                </label>
                <input
                  id="date_of_death"
                  type="date"
                  value={formData.date_of_death}
                  onChange={(e) => handleInputChange('date_of_death', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
            )}

            {/* Birth Place */}
            <div className="space-y-1.5">
              <label htmlFor="birth_place" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.birthPlace')}
              </label>
              <input
                id="birth_place"
                type="text"
                value={formData.birth_place}
                onChange={(e) => handleInputChange('birth_place', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                placeholder={t('tree.addPersonModal.birthPlacePlaceholder')}
              />
            </div>

            {/* Death Place */}
            {!isLiving && (
              <div className="space-y-1.5">
                <label htmlFor="death_place" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                  {t('tree.addPersonModal.deathPlace')}
                </label>
                <input
                  id="death_place"
                  type="text"
                  value={formData.death_place}
                  onChange={(e) => handleInputChange('death_place', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  placeholder={t('tree.addPersonModal.deathPlacePlaceholder')}
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-1.5">
              <label htmlFor="notes" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.detailsPanel.notes')}
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                placeholder={t('tree.addPersonModal.notesPlaceholder')}
              />
            </div>
          </div>
        </form>

        {/* Actions */}
        <div className="bg-slate-50 dark:bg-slate-800/50 px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-4 flex gap-2 sm:gap-2 md:gap-3 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-xs sm:text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('actions.cancel')}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSaving || !formData.first_name.trim() || !formData.last_name.trim()}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-primary text-white rounded-lg text-xs sm:text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 sm:gap-2"
          >
            {isSaving ? (
              <>
                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="hidden xs:inline sm:inline">{t('tree.addPersonModal.addingPerson')}</span>
                <span className="inline xs:hidden sm:hidden">...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base sm:text-lg">person_add</span>
                <span className="hidden xs:inline sm:inline">{t('tree.addPersonModal.addPerson')}</span>
                <span className="inline xs:hidden sm:hidden">Add</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
